import { convertSelectRangeOptionsToMap } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import { LienTypeOption } from 'pages/LoanStructure/interfaces'
import type { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { isEmpty } from 'utils'

import { DISABLE_CLOSING_COST_ESTIMATE } from './company.constants'
import { customOptionLabels, editableOptionFields } from './fieldOptions'
import type { InputType } from './input.type.constants'
import { isCustomFieldKey } from './loan.input.fields.constants'
import { IVisibleProp } from './loan.input.visibility.type'

export const finresiMapConstants: Record<string, any> = {
  citizen: {
    UsCitizen: 'US Citizen',
    PermanentResidentAlien: 'Permanent Resident',
    NonPermanentResidentAlien: 'Non Permanent Resident',
    ForeignNational: 'Foreign National',
    ITIN: 'ITIN',
  },
}

export const EscrowTypeLabels: Record<string, string> = {
  notWaived: 'Not Waived',
  waiveBoth: 'Waive Both',
  waiveInsurance: 'Waive Insurance',
}

export const MINVALUE: number = 999999999

export enum ProductTypes {
  Bridge = 'Bridge',
  FixAndFlip = 'Fix and Flip',
  GroundUpConstruction = 'Ground Up Construction',
  DSCR = 'DSCR (Long Term Rental)',
  NonQM = 'NonQM',
}

export const productTypeOptions = [
  ProductTypes.Bridge,
  ProductTypes.FixAndFlip,
  ProductTypes.GroundUpConstruction,
  ProductTypes.DSCR,
  ProductTypes.NonQM,
]

export const productTypeOptionsMap: Record<ProductTypes, string> = {
  [ProductTypes.Bridge]: 'Bridge',
  [ProductTypes.FixAndFlip]: 'Fix & Flip / Fix to Rent',
  [ProductTypes.GroundUpConstruction]: 'Ground Up Construction',
  [ProductTypes.DSCR]: 'DSCR (Rental Loan)',
  [ProductTypes.NonQM]: 'Non-QM',
}

const builtInProductTypes = new Set<string>(Object.values(ProductTypes))

const isSelectOption = (option: unknown): option is ISelectOption =>
  !!option && typeof (option as ISelectOption).key === 'string'

const productTypeOptionList = (fieldProperties?: Record<string, IVisibleProp>) => {
  const configured = fieldProperties?.productType?.options
  return Array.isArray(configured) ? configured.filter(isSelectOption) : []
}

export const getMappedProductType = (productType: string, fieldProperties?: Record<string, IVisibleProp>): string => {
  if (!productType) return productType
  const productOption = productTypeOptionList(fieldProperties).find((opt: any) => `${opt?.key}` === `${productType}`)
  const mapped = String(productOption?.mapTo || '').trim()
  return mapped || productType
}

export const isCustomProductType = (productType: string, fieldProperties?: Record<string, IVisibleProp>): boolean => {
  if (!productType || builtInProductTypes.has(productType)) return false
  const productOption = productTypeOptionList(fieldProperties).find((opt: any) => `${opt?.key}` === `${productType}`)
  return !!productOption
}

/** Built-in product types plus configured custom types, with the titles saved on Loan Field Properties. */
export const getProductTypeOptionsMap = (fieldProperties?: Record<string, IVisibleProp>): Record<string, string> => {
  const options: Record<string, string> = { ...productTypeOptionsMap }
  const configured = productTypeOptionList(fieldProperties)
  if (!configured.length) return options
  configured.forEach((opt) => {
    const key = String(opt.key || '').trim()
    if (!key) return
    if (opt.visible === false) {
      delete options[key]
      return
    }
    options[key] = String(opt.title || '').trim() || options[key] || key
  })
  return Object.keys(options).length ? options : { ...productTypeOptionsMap }
}

export const getProductTypeOptions = (fieldProperties?: Record<string, IVisibleProp>): string[] =>
  Object.keys(getProductTypeOptionsMap(fieldProperties))

export const getProductTypeLabel = (
  productType?: string | null,
  fieldProperties?: Record<string, IVisibleProp>,
): string => {
  if (!productType) return ''
  const configured = productTypeOptionList(fieldProperties).find((opt) => `${opt.key}` === `${productType}`)
  const title = String(configured?.title || '').trim()
  if (title) return title
  return (productTypeOptionsMap as Record<string, string>)[productType] || productType
}

export const enum DocType {
  FULL_DOC = 'fullDoc',
  FULL_DOC_12_MONTHS = 'fullDoc12Months',
  TEN_99_12_MONTHS = 'ten9912Months',
  TEN_99_24_MONTHS = 'ten9924Months',
  BANK_STATEMENT_ONE_MONTH_PERSONAL = 'bankStatementOneMonthPersonal',
  BANK_STATEMENT_3_MONTHS_PERSONAL = 'bankStatement3MonthsPersonal',
  BANK_STATEMENT_3_MONTHS_BUSINESS = 'bankStatement3MonthsBusiness',
  BANK_STATEMENT_12_MONTHS_PERSONAL = 'bankStatement12MonthsPersonal',
  BANK_STATEMENT_12_MONTHS_BUSINESS = 'bankStatement12MonthsBusiness',
  BANK_STATEMENT_24_MONTHS_PERSONAL = 'bankStatement24MonthsPersonal',
  BANK_STATEMENT_24_MONTHS_BUSINESS = 'bankStatement24MonthsBusiness',
  ASSET_DEPLETION_UTILIZATION = 'assetDepletionUtilization',
  VOE = 'voe',
  PNL_12_MONTHS_CPA_PREPARED = 'pnl12MonthsCpaPrepared',
  PNL_24_MONTHS_CPA_PREPARED = 'pnl24MonthsCpaPrepared',
  PNL_12_MONTHS_W_BANK_STATEMENT = 'pnl12MonthsWBankStatement',
  PNL_24_MONTHS_W_BANK_STATEMENT = 'pnl24MonthsWBankStatement',
  DSCR_NO_RATIO_DSCR = 'dscrNoRatioDscr',
  NO_INCOME_NO_EMPLOYMENT_NO_DTI = 'noIncomeNoEmploymentNoDti',
  ASSET_QUALIFIER = 'assetQualifier',
  ATR_IN_FULL = 'atrInFull',
}

export const docTypeOptions: Record<DocType, string> = {
  [DocType.FULL_DOC]: 'Full Doc',
  [DocType.FULL_DOC_12_MONTHS]: 'Full Doc 12 Months',
  [DocType.TEN_99_12_MONTHS]: '1099 12 Months',
  [DocType.TEN_99_24_MONTHS]: '1099 24 Months',
  [DocType.BANK_STATEMENT_ONE_MONTH_PERSONAL]: 'Bank Statement 1 Month Personal',
  [DocType.BANK_STATEMENT_3_MONTHS_PERSONAL]: 'Bank Statement 3 Months Personal',
  [DocType.BANK_STATEMENT_3_MONTHS_BUSINESS]: 'Bank Statement 3 Months Business',
  [DocType.BANK_STATEMENT_12_MONTHS_PERSONAL]: 'Bank Statement 12 Months Personal',
  [DocType.BANK_STATEMENT_12_MONTHS_BUSINESS]: 'Bank Statement 12 Months Business',
  [DocType.BANK_STATEMENT_24_MONTHS_PERSONAL]: 'Bank Statement 24 Months Personal',
  [DocType.BANK_STATEMENT_24_MONTHS_BUSINESS]: 'Bank Statement 24 Months Business',
  [DocType.ASSET_DEPLETION_UTILIZATION]: 'Asset Depletion / Utilization',
  [DocType.VOE]: 'VOE',
  [DocType.PNL_12_MONTHS_CPA_PREPARED]: 'PnL 12 Months CPA Prepared',
  [DocType.PNL_24_MONTHS_CPA_PREPARED]: 'PnL 24 Months CPA Prepared',
  [DocType.PNL_12_MONTHS_W_BANK_STATEMENT]: 'PnL 12 Months w/ Bank Statement',
  [DocType.PNL_24_MONTHS_W_BANK_STATEMENT]: 'PnL 24 Months w/ Bank Statement',
  [DocType.DSCR_NO_RATIO_DSCR]: 'DSCR / No Ratio DSCR',
  [DocType.NO_INCOME_NO_EMPLOYMENT_NO_DTI]: 'No Income / No Employment / No DTI',
  [DocType.ASSET_QUALIFIER]: 'Asset Qualifier',
  [DocType.ATR_IN_FULL]: 'ATR In Full',
}

export enum LoanPurposeType {
  BUSINESS_PURPOSE = 'BUSINESS_PURPOSE',
  CONSUMER_PURPOSE = 'CONSUMER_PURPOSE',
}

export const loanPurposeTypeOptions: Record<LoanPurposeType, string> = {
  [LoanPurposeType.BUSINESS_PURPOSE]: 'Business Purpose',
  [LoanPurposeType.CONSUMER_PURPOSE]: 'Consumer Purpose',
}

export const lockTypeOptions = ['Locked', 'Float', 'Expired']

/**
 * Default-rate slices of the servicing book. The keys are what the pipeline/report query sends, and
 * the backend reads them straight off `LoanFiltersDto.defaultRate`.
 *
 * The three states are kept apart because they answer different questions, and a loan can sit in
 * any of them: a rate can be configured on the loan, Default Interest can be switched on, and the
 * rate can actually be running. A converted book routinely carries a default rate on every
 * delinquent loan with the switch off, so filtering on "accruing" alone would report a clean
 * portfolio.
 */
export const defaultRateFilterOptions: Record<string, string> = {
  configured: 'Default rate configured',
  accruing: 'Accruing default interest',
  enabled: 'Default interest switched on',
  off: 'Configured but not charged',
}

// Servicing Hold, set per loan on Servicing Information. On hold the platform applies no payments,
// sends no statements and sends the borrower nothing, so which loans are held is a list a servicer
// needs to read off the book rather than one loan at a time.
export const holdFilterOptions: Record<string, string> = {
  on: 'On hold',
  off: 'Not on hold',
}

export const transactionTypeOptions = ['Purchase', 'Refinance']

export const howHearAboutUsOpions = ['Rehab Wallet', 'Networking Event', 'Referral', 'Social Media/Website', 'Other']

export const preferredCommunicationOptions = ['Phone', 'Email', 'Text', 'Web Portal']

export const loanCustomRangeOptionFields: string[] = [
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'experience',
  'forbearanceLoanModification',
  'bankruptcy',
  'mortgageLates',
]
export const loanCustomSelectOptionFields: string[] = ['loanTerm', 'prepaymentPenalty']
export { customOptionLabels, editableFieldOptions, editableOptionFields } from './fieldOptions'

export const ltvFieldKeys: string[] = ['ltv', 'cltv', 'ltc']

export const loanOptionsConstants = {
  interestType: ['Dutch', 'Non Dutch', 'Non Dutch Excluding IR'],
  entityType: [
    'Limited Liability Company',
    'Limited Liability Partnership',
    'Corporation',
    'Partnership',
    'Individual',
  ],
  loanPurposeType: loanPurposeTypeOptions,
  productType: productTypeOptionsMap,
  docType: docTypeOptions,
  lienType: LienTypeOption,
  transactionType: transactionTypeOptions,
  loanTerm: {
    6: '6 Months',
    12: '12 Months',
    18: '18 Months',
    24: '24 Months',
  },
  dscrLoanTerm: {
    180: '15 Years',
    240: '20 Years',
    360: '30 Years',
    480: '40 Years',
  },
  escrowType: ['Not Waived', 'Waive Both', 'Waive Insurance'],
  borrowerType: ['LLC', 'LLP', 'Corp', 'Partnership', 'Revocable Trust', 'Individual'],
  proposedOccupancy: [
    'Owner Occupied',
    'Second Home',
    'Non Owner Occupied',
    'Partially Owner Occupied',
    'Vacant',
    'Partially Vacant',
  ],
  prepaymentLong: {
    '0-0': '0 Month',
    '12-5': '12 Months',
    '24-5/5': '24 Months',
    '36-5/5/5': '36 Months',
    '48-5/5/5/5': '48 Months',
    '60-5/5/5/5/5': '60 Months',
  },
  propertyType: [
    'SFR-Detached',
    'SFR-Attached',
    'Condo',
    '2-4 Units',
    'PUD',
    'Mixed-Use',
    'Modular',
    'Multifamily 5+',
    'Industrial',
    'Manufactured',
    'Self Storage',
    'Office',
    'Retail',
    'Warehouse',
    'Automotive',
    'Vacant Land',
  ],
  condoType: ['Warrantable', 'Non-Warrantable', 'Condotel', 'Co-Op'],
  bankruptcy: {
    'null-null-null': 'None',
    '0-null-null': 'BK Active',
    '7-1-11': 'BK7 Discharged 1-11 Months',
    '7-12-23': 'BK7 Discharged 12-23 Months',
    '7-24-35': 'BK7 Discharged 24-35 Months',
    '7-36-47': 'BK7 Discharged 36-47 Months',
    '7-48-null': 'BK7 Discharged 48+ Months',
    '13-1-11': 'BK13 Discharged 1-11 Months',
    '13-12-23': 'BK13 Discharged 12-23 Months',
    '13-24-35': 'BK13 Discharged 24-35 Months',
    '13-36-47': 'BK13 Discharged 36-47 Months',
    '13-48-null': 'BK13 Discharged 48+ Months',
  },
  loannex2: {
    'null-null': 'None',
    '1-11': '1-11 Months',
    '12-23': '12-23 Months',
    '24-35': '24-35 Months',
    '36-47': '36-47 Months',
    '48-null': '48+ Months',
  },
  mortgageLates: {
    '0-0-30-12': '0X30X12',
    '1-1-30-12': '1X30X12',
    '0-0-60-12': '0X60X12',
    '1-null-60-12': '>= 1X60X12',
  },
  fullMortgageLates: {
    '1X30X12': '30 days late in the last 12 months.',
    '>= 2X30X12': '30 days late multiple times in the last 12 months.',
    '1X60X12': '60 days late in the last 12 months.',
    '>= 2X60X12': '60 days late multiple times in the last 12 months.',
    '1X90X12': 'More than 60 days late in the last 12 months.',
    '>= 2X90X12': 'More than 60 days late multiple times in the last 12 months.',
    '1X30X24': '30 days late in the last 24 months.',
    '>= 2X30X24': '30 days late multiple times in the last 24 months.',
    '1X60X24': '60 days late in the last 24 months.',
    '>= 2X60X24': '60 days late multiple times in the last 24 months.',
    '1X90X24': 'More than 60 days late in the last 24 months.',
    '>= 2X90X24': 'More than 60 days late multiple times in the last 24 months.',
  },
  forbearance: { 'null-null': 'None', 'null-6': '<=6 Months', '7-24': '7-24 Months', '25-null': '25+ Months' },
  states: [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'MA',
    'ME',
    'MD',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VI',
    'VT',
    'WA',
    'WV',
    'WI',
    'WY',
  ],
  hmda: {
    1: 'Provided By Borrower',
    2: 'Not Provided In Mail Telephone Or Internet Application',
    3: 'Collected On The Basis Of Visual Observation Or Surname',
  },
  citizenship: ['US Citizen', 'Permanent Resident', 'Non Permanent Resident', 'Foreign National', 'ITIN'],
  experience: {
    '0-0': '0 Transaction',
    '1-1': '1 Transaction',
    '2-2': '2 Transactions',
    '3-3': '3 Transactions',
    '4-4': '4 Transactions',
    '5-null': '5+ Transactions',
  },
  isMixedUseProperty: {
    '1': 'Yes',
    '2': 'No',
  },
}

export const propertyTypesVacantNeeded = ['Mixed-Use', 'Multifamily 5+']
export const vacantOptions = ['Number', 'Percentage']

const toBoolean = (value: any) => value === true || value === 'true' || value === 1 || value === '1'

const getVacantLabel = (amount: any, isPercentage: any) => {
  const percentage = toBoolean(isPercentage)
  const amountLabel = isEmpty(amount) ? '' : `${amount}${percentage ? '%' : ''}`
  return amountLabel
}

export const convertValueToLabel = (
  key: string,
  value: any,
  fieldProperties: Record<string, IVisibleProp>,
  targetProperty: any = null,
) => {
  let label = isEmpty(value) ? '' : String(value)

  const fieldOptions =
    fieldProperties && fieldProperties[key]?.options && !!fieldProperties[key]?.options?.length
      ? convertSelectRangeOptionsToMap(fieldProperties[key].options)
      : {}

  switch (key) {
    case 'productType':
      label = fieldOptions[value] || (loanOptionsConstants.productType as any)[value] || label
      break
    case 'loanPurposeType':
      label = (loanOptionsConstants.loanPurposeType as any)[value]
      break
    case 'docType':
      label = (loanOptionsConstants.docType as any)[value]
      break
    case 'lienType':
      label = (LienTypeOption as any)[value]
      break
    case 'loanTerm':
      label =
        fieldOptions[value] ||
        (loanOptionsConstants.loanTerm as any)[value] ||
        (loanOptionsConstants.dscrLoanTerm as any)[value]
      break
    case 'experience':
      label = fieldOptions[value] || (loanOptionsConstants.experience as any)[value]
      break
    case 'foreclosure':
    case 'deedInLieu':
    case 'shortSale':
      label = fieldOptions[value] || (loanOptionsConstants.loannex2 as any)[value]
      break
    case 'mortgageLates':
      label = fieldOptions[value] || (loanOptionsConstants.mortgageLates as any)[value]
      break
    case 'forbearanceLoanModification':
      label = fieldOptions[value] || (loanOptionsConstants.forbearance as any)[value]
      break
    case 'prepaymentPenalty':
      label = fieldOptions[value] || (loanOptionsConstants.prepaymentLong as any)[value]
      break
    case 'bankruptcy':
      label = fieldOptions[value] || (loanOptionsConstants.bankruptcy as any)[value]
      break
    case 'propertyUnderConstruction':
    case 'isPropertyInLeasableState':
    case 'firstTimeHomeBuyer':
    case 'firstTimeHomeInvestor':
    case 'ruralProperty':
    case 'propertyHasBeenListed':
    case 'isShortTermRental':
    case 'decliningMarketProperty':
    case 'isCEMATransaction':
    case 'isNewConstructionProperty':
    case 'changeToSquareFootage':
    case 'isNoScore':
    case 'isMultipleProperties':
    case 'isBPiece':
    case 'includeInterestReserve':
    case 'chargeInterestOnReserve':
      label = isEmpty(value) ? '' : !!value ? 'Yes' : 'No'
      break
    case 'vacant':
      label = getVacantLabel(value, targetProperty?.vacantIsPercentage)
      break
  }

  if (editableOptionFields.includes(key)) {
    const renamed = customOptionLabels(fieldProperties?.[key]?.options as any, false)
    if (renamed?.[String(value)]) label = renamed[String(value)]
  }

  if (isCustomFieldKey(key)) {
    switch (fieldProperties[key].inputType) {
      case 'ToggleButton':
      case 'togglebutton':
      case 'CheckBox':
      case 'checkbox':
        label = isEmpty(value) ? '' : !!value ? 'Yes' : 'No'
        break
      case 'select':
      case 'Select':
        const targetOption = Array.isArray(fieldProperties[key]?.options)
          ? (fieldProperties[key]?.options as any)?.find((option: any) => option?.key == value)
          : null
        label = targetOption?.title || (fieldProperties[key].options as any)[value] || ''
        break
    }
  }

  return label
}

export const getLoanFieldOption = (key: string, fieldProperties: Record<string, IVisibleProp>) => {
  const fieldOptions =
    fieldProperties && fieldProperties[key]?.options && !!fieldProperties[key]?.options?.length
      ? convertSelectRangeOptionsToMap(fieldProperties[key].options)
      : {}

  switch (key) {
    case 'productType':
      return (!isEmpty(fieldOptions) ? fieldOptions : loanOptionsConstants.productType) as Record<string, any>
    case 'lienType':
      return LienTypeOption as Record<string, any>
    case 'loanTerm':
      return (fieldOptions || loanOptionsConstants.loanTerm || loanOptionsConstants.dscrLoanTerm) as Record<string, any>
    case 'experience':
      return (fieldOptions || loanOptionsConstants.experience) as Record<string, any>
    case 'foreclosure':
    case 'deedInLieu':
    case 'shortSale':
      return (fieldOptions || loanOptionsConstants.loannex2) as Record<string, any>
    case 'mortgageLates':
      return (fieldOptions || loanOptionsConstants.mortgageLates) as Record<string, any>
    case 'forbearanceLoanModification':
      return (fieldOptions || loanOptionsConstants.forbearance) as Record<string, any>
    case 'prepaymentPenalty':
      return (fieldOptions || loanOptionsConstants.prepaymentLong) as Record<string, any>
    case 'bankruptcy':
      return (fieldOptions || loanOptionsConstants.bankruptcy) as Record<string, any>
  }

  return {}
}

export const getInitialLoanFields = (forPdf: boolean = false, allowCents: boolean = false) => {
  let rlt: Record<string, InputType> = {
    productType: {
      title: 'Product Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.productType,
      error: '',
      required: true,
    },
    transactionType: {
      title: 'Transaction Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.transactionType,
      error: '',
      required: true,
    },
    docType: {
      title: 'Doc Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.docType,
      error: '',
      required: true,
    },
    loanPurposeType: {
      title: 'Loan Purpose',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.loanPurposeType,
      error: '',
    },
    lienType: {
      title: 'Lien Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: LienTypeOption,
      error: '',
      required: true,
    },
    interestType: {
      title: 'Interest Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.interestType,
      error: '',
      required: true,
    },
    isMultipleProperties: {
      title: 'Financing of Additional Properties?',
      inputType: 'check',
      error: '',
      required: true,
    },
    howManyProperties: {
      key: 'howManyProperties',
      title: 'How Many Properties?',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    propertyUnderConstruction: {
      title: 'Property Under Construction?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isPropertyInLeasableState: {
      title: 'Can property be rented as is?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isShortTermRental: {
      title: 'Is the property a short term rental?',
      inputType: 'check',
      error: '',
      required: true,
    },
    decliningMarketProperty: {
      title: 'Is the property located in a declining market?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isNewConstructionProperty: {
      title: 'Is the property a new construction?',
      inputType: 'check',
      error: '',
      required: true,
    },
    changeToSquareFootage: {
      title: 'Will there be a change to Square Footage?',
      inputType: 'check',
      error: '',
      required: true,
    },
    proposedOccupancy: {
      title: 'Occupancy',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.proposedOccupancy,
      error: '',
      required: true,
    },
    loanTerm: {
      title: 'Loan Term',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.loanTerm,
      error: '',
      required: true,
    },
    borrowerType: {
      title: 'Borrower Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.borrowerType,
      error: '',
      required: true,
    },
    underwritingFee: {
      title: 'Underwriting Fee',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
    },
    proposedLoanAmount: {
      title: 'Proposed Loan Amount',
      inputType: 'text',
      key: 'proposedLoanAmount',
      type: allowCents ? 'thousandSep' : 'thousandSepNoDecimal',
      error: '',
      prefix: '$',
      required: true,
    },
    isBPiece: {
      title: 'B Piece?',
      inputType: 'check',
      error: '',
      required: true,
    },
    aPieceAmount: {
      title: 'A Piece',
      inputType: 'text',
      type: allowCents ? 'thousandSep' : 'thousandSepNoDecimal',
      error: '',
      prefix: '$',
      required: true,
    },
    ltv: {
      title: 'LTV',
      inputType: 'text',
      prefix: '%',
    },
    cltv: {
      title: 'CLTV',
      inputType: 'text',
      prefix: '%',
      readOnly: true,
    },
    ltc: {
      title: 'LTC',
      inputType: 'text',
      prefix: '%',
    },
    propertyPurchasePrice: {
      title: 'Property Purchase Price',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    asIsValue: {
      title: 'As Is Appraised Value', // 'As Is Value', Original Label
      key: 'asIsValue',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    afterRepairValue: {
      title: 'After Repair Value',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    rehabBudget: {
      title: 'Construction Budget',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    constructionReserve: {
      title: 'Construction Amount Financed',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    costsSpentToDate: {
      title: 'Costs Spent To Date',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    interestReserve: {
      title: 'Interest Reserve',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
      tooltip: 'The dollar amount of interest payment(s) being financed into the loan amount.',
    },
    chargeInterestOnReserve: {
      title: 'Charge Interest on Interest Reserve?',
      inputType: 'check',
      error: '',
      tooltip:
        'Non-Dutch loans only. On: the interest reserve is financed into principal at closing and accrues interest from day one. Off: it is held out of the balance and advanced as the Interest Reserve Ledger pays it out. The construction holdback still accrues draw by draw either way.',
    },
    includeInterestReserve: {
      title: 'Include Interest Reserve in LTC?',
      inputType: 'check',
      error: '',
      required: true,
    },
    experience: {
      title: 'Experience',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.experience,
      error: '',
      required: true,
    },
    howManyExperiences: {
      title: 'How Many Experiencies?',
      inputType: 'select',
      hasDefaultOption: true,
      options: {},
      error: '',
      required: true,
    },
    floodInsurance: {
      title: 'Flood Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    schoolTax: {
      title: 'School Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    otherTax: {
      title: 'Other Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyTaxes: {
      title: 'Proposed Monthly Taxes',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyInsurance: {
      title: 'Proposed Monthly Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyRent: {
      title: 'Proposed Monthly Rent',
      key: 'proposedMonthlyRent',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyHoaDues: {
      title: 'Proposed Monthly HOA Dues',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    propertyType: {
      title: 'Property Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.propertyType,
      error: '',
      required: true,
    },
    condoType: {
      title: 'Condo Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.condoType,
      error: '',
      required: true,
    },
    prepaymentPenalty: {
      title: 'Prepayment Penalty',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.prepaymentLong,
      error: '',
      required: true,
    },
    propertyHasBeenListed: {
      title: 'Has property been listed in the last 6 months?',
      inputType: 'check',
      error: '',
      required: true,
    },
    residency: {
      title: 'Citizenship',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.citizenship,
      error: '',
      required: true,
    },
    amortizationType: {
      title: 'Amortization Type',
      inputType: 'select',
      key: 'amortizationType',
      hasDefaultOption: true,
      options: ['ARM', 'Fixed', 'IO - ARM', 'IO - Fixed'],
      error: '',
      required: true,
    },
    escrowType: {
      title: 'Escrow Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.escrowType,
      error: '',
      required: true,
    },
    firstTimeHomeBuyer: {
      title: 'First Time Home Buyer?',
      inputType: 'check',
      error: '',
      required: true,
    },
    firstTimeHomeInvestor: {
      title: 'First Time Home Investor?',
      inputType: 'check',
      error: '',
      required: true,
    },
    ruralProperty: {
      title: 'Rural Property?',
      inputType: 'check',
      error: '',
      required: true,
    },
    bankruptcy: {
      title: 'Bankruptcy',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.bankruptcy,
      error: '',
      required: true,
    },
    foreclosure: {
      title: 'Foreclosure',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    deedInLieu: {
      title: 'Deed-In-Lieu',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    shortSale: {
      title: 'Short Sale',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    mortgageLates: {
      title: 'Pay History',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.mortgageLates,
      error: '',
      required: true,
    },
    forbearanceLoanModification: {
      title: 'Forbearance / Loan Modification',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.forbearance,
      error: '',
      required: true,
    },
    secondLien: {
      title: 'Additional Lien',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    secondLienPayment: {
      title: 'Additional Lien Payment',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    existingLienholder: {
      title: 'Existing Lienholder',
      inputType: 'text',
      type: 'text',
      error: '',
      tooltip: 'Who holds the senior lien the Additional Lien balance belongs to.',
    },
    totalCombinedDebt: {
      title: 'Total Combined Debt',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      readOnly: true,
      tooltip: 'Loan Amount + Additional Lien. The debt CLTV is measured on.',
    },
    lienPayoff: {
      title: 'Lien Payoff',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    closingCostEstimate: {
      title: 'Closing Cost Estimate',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
      readOnly: DISABLE_CLOSING_COST_ESTIMATE ? false : true, // Enable to Pull Closing Cost Estimate
    },
    escrowRepairHoldback: {
      title: 'Escrow Repair Holdback',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    monthsReserve: {
      title: 'Months Reserve',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    estimatedDscr: {
      title: 'DSCR',
      inputType: 'text',
      type: 'number',
      error: '',
      visible: false,
    },
    numberOfUnits: {
      title: 'Number of Units',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    vacant: {
      title: 'Vacant Units',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      required: true,
    },
    borrowerFirstName: {
      title: 'Guarantor First Name',
      inputType: 'text',
      error: '',
      required: true,
    },
    borrowerMiddleName: {
      title: 'Guarantor Middle Name',
      inputType: 'text',
      error: '',
    },
    borrowerLastName: {
      title: 'Guarantor Last Name',
      inputType: 'text',
      error: '',
      required: true,
    },
    isNoScore: {
      title: 'No Score',
      inputType: 'checkbox',
      color: 'gray',
      className: 'mt-6',
      error: '',
    },
    estimatedCreditScore: {
      title: 'Credit Score',
      key: 'estimatedCreditScore',
      inputType: 'text',
      type: 'creditScore',
      error: '',
      required: true,
    },
    propertyState: {
      title: 'State',
      inputType: 'select',
      hasDefaultOption: true,
      options: [...loanOptionsConstants.states],
      error: '',
      required: true,
    },
    isCEMATransaction: {
      title: 'Is this transaction a CEMA?',
      inputType: 'check',
      error: '',
      required: true,
    },
    subjectPropertyAddress: {
      title: 'Subject Property Address',
      inputType: 'Map',
      sitexPro: true,
      error: '',
      required: true,
      length: 1,
    },
    additionalPropertyAddress: {
      title: 'Additional Subject Property Address',
      inputType: 'AutoComplete',
      autoCompleteType: 'map',
      error: '',
      length: 1,
    },
    subjectPropertyMap: {
      title: 'Subject Property Address Map',
      inputType: 'GoogleMap',
      length: 1,
    },
    ltvAdjustment: {
      title: 'LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    cltvAdjustment: {
      title: 'CLTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    aivLtvAdjustment: {
      title: 'AIV-LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    arvLtvAdjustment: {
      title: 'ARV-LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    ltcAdjustment: {
      title: 'LTC Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    ltpAdjustment: {
      title: 'LTP Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
  }
  if (forPdf) {
    Object.keys(rlt).forEach((key) => {
      if (rlt[key].inputType.toLowerCase() === 'select') {
        rlt[key].inputType = 'text'
      }
    })
  }
  return rlt
}

export const fieldsByTransactionTypeAndProductTypeConstant: Record<ProductTypes, Record<string, string[]>> = {
  [ProductTypes.Bridge]: {
    Purchase: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'propertyUnderConstruction',
      'isMultipleProperties',
      'isPropertyInLeasableState',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'ruralProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'isMultipleProperties',
      'propertyUnderConstruction',
      'isPropertyInLeasableState',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'ruralProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  [ProductTypes.FixAndFlip]: {
    Purchase: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'costsSpentToDate',
      'includeInterestReserve',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'changeToSquareFootage',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'costsSpentToDate',
      'includeInterestReserve',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'changeToSquareFootage',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  [ProductTypes.GroundUpConstruction]: {
    Purchase: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'costsSpentToDate',
      'includeInterestReserve',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'loanPurposeType',
      'interestType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'ltc',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'costsSpentToDate',
      'includeInterestReserve',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  [ProductTypes.DSCR]: {
    Purchase: [
      'lienType',
      'loanPurposeType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'propertyPurchasePrice',
      'asIsValue',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'prepaymentPenalty',
      'residency',
      'amortizationType',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
    Refinance: [
      'lienType',
      'loanPurposeType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'asIsValue',
      'lienPayoff',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'prepaymentPenalty',
      'propertyHasBeenListed',
      'residency',
      'amortizationType',
      'experience',
      'howManyExperiences',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
  },
  [ProductTypes.NonQM]: {
    Purchase: [
      'docType',
      'lienType',
      'loanPurposeType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'propertyPurchasePrice',
      'asIsValue',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'prepaymentPenalty',
      'residency',
      'amortizationType',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
    Refinance: [
      'docType',
      'lienType',
      'loanPurposeType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'underwritingFee',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'ltv',
      'cltv',
      'asIsValue',
      'lienPayoff',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'propertyType',
      'numberOfUnits',
      'vacant',
      'prepaymentPenalty',
      'propertyHasBeenListed',
      'residency',
      'amortizationType',
      'experience',
      'howManyExperiences',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
  },
}

export const groupedFields = [
  [
    'productType',
    'transactionType',
    'loanPurposeType',
    'docType',
    'lienType',
    'interestType',
    'propertyUnderConstruction',
    'isPropertyInLeasableState',
    'isShortTermRental',
    'decliningMarketProperty',
    'isNewConstructionProperty',
    'changeToSquareFootage',
    'isMultipleProperties',
    'howManyProperties',
    'isBPiece',
  ],
  ['proposedOccupancy', 'loanTerm', 'prepaymentPenalty', 'propertyHasBeenListed'],
  [
    'propertyPurchasePrice',
    'asIsValue',
    'afterRepairValue',
    'interestReserve',
    'includeInterestReserve',
    'chargeInterestOnReserve',
    'secondLien',
    'secondLienPayment',
    'existingLienholder',
    'lienPayoff',
    'underwritingFee',
    'rehabBudget',
    'constructionReserve',
    'costsSpentToDate',
    'escrowRepairHoldback',
    'closingCostEstimate',
    'proposedLoanAmount',
    'aPieceAmount',
    'ltv',
    'cltv',
    'totalCombinedDebt',
    'ltc',
  ],
  ['experience', 'howManyExperiences'],
  ['amortizationType', 'escrowType'],
  ['isNoScore', 'propertyState', 'estimatedCreditScore', 'isCEMATransaction'],
  ['propertyType', 'condoType', 'numberOfUnits', 'vacant'],
  ['firstTimeHomeBuyer', 'firstTimeHomeInvestor', 'ruralProperty'],
  [
    'bankruptcy',
    'foreclosure',
    'deedInLieu',
    'shortSale',
    'mortgageLates',
    'forbearanceLoanModification',
    'residency',
    'monthsReserve',
  ],
  ['borrowerType', 'borrowerFirstName', 'borrowerMiddleName', 'borrowerLastName'],
  [
    'proposedMonthlyRent',
    'proposedMonthlyTaxes',
    'proposedMonthlyInsurance',
    'proposedMonthlyHoaDues',
    'floodInsurance',
    'schoolTax',
    'otherTax',
    'estimatedDscr',
  ],
  ['subjectPropertyAddress', 'additionalPropertyAddress', 'subjectPropertyMap'],
  ['ltvAdjustment', 'cltvAdjustment', 'aivLtvAdjustment', 'arvLtvAdjustment', 'ltcAdjustment', 'ltpAdjustment'],
]

export const OwnershipTypes = ['Own', 'Rent', 'No Primary Housing Expense']

export const YesNoOptions = {
  yes: 'Yes',
  no: 'No',
}

export const YearOptions = {
  '1': '1 year',
  '2': '2 years',
  '3': '3 years',
  '4': '4 years',
  '5': '5 years',
  '6': '6 years',
  '7': '7 years',
  '8': '8 years',
  '9': '9 years',
  '10': '10+ years',
}

export const OwnRentYearOptions = {
  '1': '< 2 Years',
  '2': '>= 2 Years',
}

export const OwnRentYearOptionsRev: Record<string, string> = Object.fromEntries(
  Object.entries(OwnRentYearOptions).map(([key, value]) => [value, key]),
)

export const MaritalStatusTypes: Record<string, string> = {
  Married: 'Married (includes registered domestic partners)',
  Unmarried: 'Unmarried (includes divorced, widow)',
  Single: 'Single (never been married)',
  Separated: 'Separated',
}

/**
 * Languages a borrower can be flagged as preferring. Broad on purpose: the point of the field is to
 * route a call or a notice to someone who can actually speak to the borrower, so a list that stops
 * at the two or three a given shop happens to see today would need editing the first time it does
 * not. Blank means nobody has recorded a preference, which is not the same as English.
 */
export const LanguageOptions = [
  'English',
  'Albanian',
  'American Sign Language',
  'Amharic',
  'Arabic',
  'Armenian',
  'Bengali',
  'Burmese',
  'Chinese (Cantonese)',
  'Chinese (Mandarin)',
  'Farsi (Persian)',
  'French',
  'German',
  'Greek',
  'Gujarati',
  'Haitian Creole',
  'Hebrew',
  'Hindi',
  'Hmong',
  'Italian',
  'Japanese',
  'Khmer',
  'Korean',
  'Lao',
  'Nepali',
  'Polish',
  'Portuguese',
  'Punjabi',
  'Romanian',
  'Russian',
  'Serbo-Croatian',
  'Somali',
  'Spanish',
  'Swahili',
  'Tagalog',
  'Thai',
  'Turkish',
  'Ukrainian',
  'Urdu',
  'Vietnamese',
  'Yiddish',
  'Other',
]

/** Language-filter sentinel meaning "every borrower who does not read or speak English". */
export const NON_ENGLISH_LANGUAGE = 'nonEnglish'

export const MaritalStatusTypesRev: Record<string, string> = Object.fromEntries(
  Object.entries(MaritalStatusTypes).map(([key, value]) => [value, key]),
)

export const priceLoanRelatedFields = [
  'transactionType',
  'interestType',
  'productType',
  'docType',
  'lienType',
  'loanTerm',
  'proposedOccupancy',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'closingCostEstimate',
  'escrowRepairHoldback',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'interestReserve',
  'includeInterestReserve',
  'constructionReserve',
  'escrowType',
  'propertyType',
  'numberOfUnits',
  'vacant',
  'propertyHasBeenListed',
  'firstTimeHomeBuyer',
  'firstTimeHomeInvestor',
  'isMultipleProperties',
  'howManyProperties',
  'propertyUnderConstruction',
  'isPropertyInLeasableState',
  'isShortTermRental',
  'ruralProperty',
  'decliningMarketProperty',
  'isCEMATransaction',
  'isNewConstructionProperty',
  'changeToSquareFootage',
  'bankruptcy',
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'mortgageLates',
  'forbearanceLoanModification',
  'secondLien',
  'secondLienPayment',
  'monthsReserve',
  'estimatedDscr',
  'condoType',
  'prepaymentPenalty',
  'residency',
  'amortizationType',
  'subjectPropertyAddress',
  'additionalPropertyAddress',
  'isNoScore',
  'estimatedCreditScore',
  'propertyState',
  'experience',
  'howManyExperiences',
  'borrowerType',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
  'ltvAdjustment',
  'cltvAdjustment',
  'aivLtvAdjustment',
  'arvLtvAdjustment',
  'ltcAdjustment',
  'ltpAdjustment',
]

export const priceLimitRelatedFields = [
  'transactionType',
  'isNoScore',
  'estimatedCreditScore',
  'productType',
  'experience',
  'howManyExperiences',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'propertyType',
  'numberOfUnits',
  'vacant',
  'constructionReserve',
  'propertyHasBeenListed',
  'firstTimeHomeInvestor',
  'firstTimeHomeBuyer',
  'ruralProperty',
  'isMultipleProperties',
  'howManyProperties',
  'propertyUnderConstruction',
  'isPropertyInLeasableState',
  'isShortTermRental',
  'decliningMarketProperty',
  'isCEMATransaction',
  'isNewConstructionProperty',
  'lienPayoff',
  'closingCostEstimate',
  'escrowRepairHoldback',
  'interestReserve',
  'includeInterestReserve',
  'residency',
  'bankruptcy',
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'mortgageLates',
  'forbearanceLoanModification',
  'propertyType',
  'subjectPropertyAddress',
  'proposedOccupancy',
  'ltvAdjustment',
  'cltvAdjustment',
  'aivLtvAdjustment',
  'arvLtvAdjustment',
  'ltcAdjustment',
  'ltpAdjustment',
  'vacantIsPercentage',
]

export const calcLTVFields = [
  'transactionType',
  'propertyPurchasePrice',
  'asIsValue',
  'constructionReserve',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'isMultipleProperties',
  'howManyProperties',
]

export const rentTaxInsuranceFields = [
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
]

export const calcCLTVFields = [...calcLTVFields, 'secondLien']

export const sandBoxModeTooltip = [
  'Sandbox Mode lets you try changes without touching the loan.',
  'Edit Loan Details and re-price against the current ratesheet to see what',
  'the change would do. Nothing is saved - switching Sandbox Mode off reverts',
  'your edits. To make a change stick, submit a Loan Change Request.',
].join('\n')

export const feeWorkSheetSandboxTooltip = [
  'Sandbox lets you try fee scenarios without touching the loan.',
  'Edit fees and see the totals recalculate. Nothing is saved - there is no',
  'Save Changes bar while Sandbox is on, and switching it off reloads the',
  'worksheet as it was last saved.',
].join('\n')

export const sandBoxNotEditableFields = [
  'borrowerType',
  'borrowerFirstName',
  'borrowerMiddleName',
  'borrowerLastName',
  // 'estimatedCreditScore',
  // 'proposedMonthlyTaxes',
  // 'proposedMonthlyInsurance',
  // 'proposedMonthlyRent',
  // 'proposedMonthlyHoaDues',
  // 'floodInsurance',
  // 'schoolTax',
  // 'otherTax',
]

export const dscrRelatedFields = [
  'productType',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'secondLien',
  'secondLienPayment',
  'proposedMonthlyInsurance',
  'proposedMonthlyTaxes',
  'proposedMonthlyHoaDues',
  'proposedMonthlyRent',
  'floodInsurance',
  'schoolTax',
  'otherTax',
]

export const MultiPropertyFields = [
  'subjectPropertyAddress',
  'transactionType',
  'lienType',
  'proposedOccupancy',
  'propertyHasBeenListed',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'constructionReserve',
  'costsSpentToDate',
  'secondLien',
  'secondLienPayment',
  'lienPayoff',
  'propertyType',
  'condoType',
  'numberOfUnits',
  'vacant',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
  'estimatedDscr',
]

export const propertySummaryFields = [
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'constructionReserve',
  'secondLien',
  'secondLienPayment',
  'lienPayoff',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
  'estimatedDscr',
]

export const DataVerifyTaxAvailableYears = Object.keys(Array(4).fill(1))
  .map((v) => new Date().getFullYear() + Number(v) - 4)
  .reverse()

export enum PipeLineType {
  Default = 'Default',
  Servicing = 'Servicing',
  Fci = 'FCI',
}

export const PipeLineTypeLabels: Record<string, string> = {
  [PipeLineType.Default]: 'Origination',
  [PipeLineType.Servicing]: 'Servicing',
  [PipeLineType.Fci]: 'Third Party Servicing',
}

export const OrdinaryPipelineTypes = [PipeLineType.Default]

export const DocumentsRefreshInterval = 5000
